/**
 * Phase 4.3: Workflow Automation - Notification Panel
 * Real-time notification display with role-based alerts
 */

import React, { useState, useEffect } from 'react';
import { useNotifications } from '../contexts/NotificationContext';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { ScrollArea } from './ui/scroll-area';
import { 
  Bell, BellRing, Check, X, Settings, Filter, 
  AlertTriangle, Clock, DollarSign, Package2,
  CheckCheck, Trash2, RotateCcw, Eye, EyeOff
} from 'lucide-react';

const NotificationPanel = ({ isOpen, onClose }) => {
  const {
    notifications,
    unreadCount,
    alertRules,
    loading,
    lastCheck,
    refreshAlerts,
    markAsRead,
    markAllAsRead,
    dismissNotification,
    clearAllNotifications,
    updateAlertRule,
    getNotifications,
    NOTIFICATION_TYPES,
    NOTIFICATION_PRIORITIES
  } = useNotifications();

  const [activeTab, setActiveTab] = useState('all');
  const [selectedPriority, setSelectedPriority] = useState('all');

  // Get filtered notifications based on current tab and filters
  const filteredNotifications = React.useMemo(() => {
    const options = {};
    
    if (activeTab === 'unread') {
      options.unreadOnly = true;
    } else if (activeTab !== 'all') {
      options.type = activeTab;
    }
    
    if (selectedPriority !== 'all') {
      options.priority = selectedPriority;
    }
    
    return getNotifications(options);
  }, [getNotifications, activeTab, selectedPriority]);

  // Priority colors and icons
  const getPriorityConfig = (priority) => {
    switch (priority) {
      case NOTIFICATION_PRIORITIES.CRITICAL:
        return { 
          color: 'bg-red-100 text-red-800 border-red-200', 
          icon: AlertTriangle,
          badge: 'destructive'
        };
      case NOTIFICATION_PRIORITIES.HIGH:
        return { 
          color: 'bg-orange-100 text-orange-800 border-orange-200', 
          icon: Bell,
          badge: 'default'
        };
      case NOTIFICATION_PRIORITIES.MEDIUM:
        return { 
          color: 'bg-yellow-100 text-yellow-800 border-yellow-200', 
          icon: Clock,
          badge: 'secondary'
        };
      case NOTIFICATION_PRIORITIES.LOW:
        return { 
          color: 'bg-blue-100 text-blue-800 border-blue-200', 
          icon: Bell,
          badge: 'outline'
        };
      default:
        return { 
          color: 'bg-gray-100 text-gray-800 border-gray-200', 
          icon: Bell,
          badge: 'secondary'
        };
    }
  };

  // Get type counts for tabs
  const getTypeCounts = () => {
    const counts = {
      all: notifications.filter(n => !n.dismissed).length,
      unread: unreadCount,
      [NOTIFICATION_TYPES.CRITICAL_STOCK]: getNotifications({ type: NOTIFICATION_TYPES.CRITICAL_STOCK }).length,
      [NOTIFICATION_TYPES.LOW_MARGIN]: getNotifications({ type: NOTIFICATION_TYPES.LOW_MARGIN }).length,
      [NOTIFICATION_TYPES.INACTIVE_SKU]: getNotifications({ type: NOTIFICATION_TYPES.INACTIVE_SKU }).length,
      [NOTIFICATION_TYPES.INCOMPLETE_DATA]: getNotifications({ type: NOTIFICATION_TYPES.INCOMPLETE_DATA }).length
    };
    return counts;
  };

  const typeCounts = getTypeCounts();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-y-0 right-0 z-50 w-96 bg-white shadow-2xl border-l border-gray-200 flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <BellRing className="w-5 h-5 text-purple-600" />
            <h2 className="text-lg font-semibold">Notifications</h2>
            {unreadCount > 0 && (
              <Badge variant="destructive" className="ml-1">
                {unreadCount}
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={refreshAlerts}
              disabled={loading}
              className="h-8 w-8 p-0"
            >
              <RotateCcw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="h-8 w-8 p-0"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>
        
        {/* Quick Actions */}
        <div className="flex items-center gap-2 mt-2">
          <Button
            variant="outline"
            size="sm"
            onClick={markAllAsRead}
            disabled={unreadCount === 0}
            className="gap-1"
          >
            <CheckCheck className="w-3 h-3" />
            Mark All Read
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={clearAllNotifications}
            disabled={notifications.length === 0}
            className="gap-1"
          >
            <Trash2 className="w-3 h-3" />
            Clear All
          </Button>
        </div>

        {/* Last Updated */}
        <p className="text-xs text-gray-500 mt-2">
          Last updated: {lastCheck.toLocaleTimeString()}
        </p>
      </div>

      {/* Tabs and Filters */}
      <div className="p-4 border-b border-gray-200">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-3 text-xs">
            <TabsTrigger value="all" className="gap-1">
              All ({typeCounts.all})
            </TabsTrigger>
            <TabsTrigger value="unread" className="gap-1">
              Unread ({typeCounts.unread})
            </TabsTrigger>
            <TabsTrigger value="critical_stock" className="gap-1">
              <Package2 className="w-3 h-3" />
              Stock ({typeCounts.critical_stock})
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Priority Filter */}
        <div className="flex items-center gap-2 mt-2">
          <Filter className="w-4 h-4 text-gray-500" />
          <select
            value={selectedPriority}
            onChange={(e) => setSelectedPriority(e.target.value)}
            className="flex-1 text-sm border rounded px-2 py-1"
          >
            <option value="all">All Priorities</option>
            <option value="critical">Critical</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
        </div>
      </div>

      {/* Notifications List */}
      <ScrollArea className="flex-1 p-4">
        <div className="space-y-3">
          {filteredNotifications.length === 0 ? (
            <div className="text-center py-8">
              <Bell className="w-12 h-12 mx-auto text-gray-300 mb-2" />
              <p className="text-gray-500">No notifications</p>
              <p className="text-xs text-gray-400">
                {activeTab === 'unread' ? 'All caught up!' : 'System is monitoring for alerts...'}
              </p>
            </div>
          ) : (
            filteredNotifications.map(notification => {
              const priorityConfig = getPriorityConfig(notification.priority);
              const IconComponent = notification.icon;

              return (
                <Card 
                  key={notification.id} 
                  className={`${priorityConfig.color} ${!notification.read ? 'ring-2 ring-blue-200' : ''} transition-all hover:shadow-md cursor-pointer`}
                  onClick={() => !notification.read && markAsRead(notification.id)}
                >
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-2">
                        <IconComponent className="w-4 h-4" />
                        <Badge variant={priorityConfig.badge} className="text-xs">
                          {notification.priority}
                        </Badge>
                        {!notification.read && (
                          <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        {!notification.read && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              markAsRead(notification.id);
                            }}
                            className="h-6 w-6 p-0 hover:bg-white/50"
                          >
                            <Check className="w-3 h-3" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            dismissNotification(notification.id);
                          }}
                          className="h-6 w-6 p-0 hover:bg-white/50"
                        >
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="space-y-1">
                      <h4 className="text-sm font-medium">{notification.title}</h4>
                      <p className="text-sm">{notification.message}</p>
                      <div className="flex items-center justify-between text-xs text-gray-600">
                        <span>{notification.product.name}</span>
                        <span>{new Date(notification.created).toLocaleTimeString()}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>
      </ScrollArea>

      {/* Alert Rules Configuration */}
      <div className="p-4 border-t border-gray-200">
        <h3 className="text-sm font-medium mb-2 flex items-center gap-2">
          <Settings className="w-4 h-4" />
          Alert Rules
        </h3>
        <div className="space-y-2">
          {alertRules.slice(0, 4).map(rule => (
            <div key={rule.id} className="flex items-center justify-between text-sm">
              <span className="flex-1 truncate">{rule.name}</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => updateAlertRule(rule.id, { enabled: !rule.enabled })}
                className="h-6 w-6 p-0"
              >
                {rule.enabled ? 
                  <Eye className="w-3 h-3 text-green-600" /> : 
                  <EyeOff className="w-3 h-3 text-gray-400" />
                }
              </Button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default NotificationPanel;